import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  DocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  FunnelIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  PlayIcon,
  SunIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { ChartBarIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { userAppContext } from '@/context/Context';
import { useUserApiKey } from '@/hooks/useUserApiKey';
import { useAuthStatus } from '@/store';
import QueriesLimitTable from '@/components/API/QueriesLimitTable';
import { CODE_SNIPPET_LABELS, CODE_SNIPPET_LANGS, SAMPLE_RESPONSE, buildCodeSnippets } from './codeSnippets';
import { buildRequestUrl, buildMultipartFormData, initialParamValues, isMultipartOperation } from './parseOpenApi';
import { useOpenApiSpec } from './useOpenApiSpec';
import { countActiveFilters, groupParameters, isMinMaxPair } from './paramGroups';
import type { ApiResponseState, CodeSnippetLang, ConsoleOperation, ConsoleParameter } from './types';
import './ApiConsole.css';

const THEME_STORAGE_KEY = 'disgenet-api-console-theme';
type ConsoleTheme = 'dark' | 'light';

function readStoredTheme(): ConsoleTheme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function formatBytes(size: number | null): string {
  if (size == null) return '—';
  if (size < 1024) return `${size} B`;
  return `${(size / 1024).toFixed(1)} KB`;
}

function MethodPill({ method }: { method: string }) {
  return <span className={`api-console-method is-${method}`}>{method.toUpperCase()}</span>;
}

function useOverflowsWhenClamped(open: boolean, contentKey: string) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => {
      if (open) return;
      setOverflows(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, contentKey]);

  return { contentRef, overflows };
}

function EndpointDescription({ html, text }: { html: string; text: string }) {
  const hasPre = /<pre[\s>]/i.test(html);
  const [expanded, setExpanded] = useState(hasPre);
  const { contentRef, overflows } = useOverflowsWhenClamped(expanded || hasPre, html || text);

  useEffect(() => {
    setExpanded(hasPre);
  }, [html, text, hasPre]);

  if (!html && !text) return null;

  const showToggle = !hasPre && (overflows || expanded);

  return (
    <div className={`api-console-desc${expanded || hasPre ? ' is-expanded' : ''}`}>
      {html ? (
        <div
          ref={contentRef}
          className="api-console-desc-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <p ref={contentRef} className="api-console-desc-content" style={{ whiteSpace: 'pre-wrap' }}>
          {text}
        </p>
      )}
      {showToggle && (
        <button type="button" className="api-console-desc-toggle" onClick={() => setExpanded(v => !v)}>
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileParamField({
  paramName,
  file,
  onFileChange,
}: {
  paramName: string;
  file?: File | null;
  onFileChange?: (file: File | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const assignFile = (candidate: File | null | undefined) => {
    onFileChange?.(candidate ?? null);
  };

  const clearFile = () => {
    assignFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  if (file) {
    return (
      <div className="api-console-file-selected">
        <div className="api-console-file-selected-main">
          <DocumentIcon className="api-console-file-icon" aria-hidden />
          <div className="api-console-file-meta">
            <span className="api-console-file-name" title={file.name}>
              {file.name}
            </span>
            <span className="api-console-file-size">{formatFileSize(file.size)}</span>
          </div>
        </div>
        <div className="api-console-file-actions">
          <button type="button" className="api-console-file-btn" onClick={() => inputRef.current?.click()}>
            Replace
          </button>
          <button
            type="button"
            className="api-console-file-btn is-icon"
            onClick={clearFile}
            aria-label={`Remove ${file.name}`}
            title="Remove file"
          >
            <XMarkIcon />
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          className="api-console-file-input"
          onChange={e => assignFile(e.target.files?.[0])}
          aria-label={`Upload ${paramName}`}
        />
      </div>
    );
  }

  return (
    <div
      className={`api-console-file-dropzone${dragging ? ' is-dragging' : ''}`}
      onDragOver={e => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => {
        e.preventDefault();
        setDragging(false);
        assignFile(e.dataTransfer.files?.[0]);
      }}
      onClick={() => inputRef.current?.click()}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Upload ${paramName}`}
    >
      <ArrowUpTrayIcon className="api-console-file-icon" aria-hidden />
      <div className="api-console-file-dropzone-copy">
        <span className="api-console-file-dropzone-title">
          Drop a file here, or <em>browse</em>
        </span>
        <span className="api-console-file-dropzone-hint">One file · multipart/form-data</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        className="api-console-file-input"
        onChange={e => assignFile(e.target.files?.[0])}
        aria-label={`Upload ${paramName}`}
      />
    </div>
  );
}

function ParamField({
  param,
  value,
  file,
  onChange,
  onFileChange,
}: {
  param: ConsoleParameter;
  value: string;
  file?: File | null;
  onChange: (val: string) => void;
  onFileChange?: (file: File | null) => void;
}) {
  if (param.in === 'body') {
    return (
      <>
        <div className="api-console-param-example">Example Value</div>
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={param.example ?? '{}'}
          spellCheck={false}
          className="api-console-body-editor"
        />
      </>
    );
  }
  if (param.type === 'file') {
    return <FileParamField paramName={param.name} file={file} onFileChange={onFileChange} />;
  }
  if (param.enumValues?.length) {
    return (
      <select value={value} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        {param.enumValues.map(option => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={param.example ?? (param.multi ? 'value1,value2' : param.name)}
    />
  );
}

function CollapsibleDesc({ html, text }: { html?: string; text?: string }) {
  const [open, setOpen] = useState(false);
  const { contentRef, overflows } = useOverflowsWhenClamped(open, html || text || '');

  if (!html && !text) return null;

  return (
    <>
      <div ref={contentRef} className={`api-console-param-desc${open ? '' : ' is-clamped'}`}>
        {html ? <span dangerouslySetInnerHTML={{ __html: html }} /> : text}
      </div>
      {(overflows || open) && (
        <button type="button" className="api-console-param-desc-more" onClick={() => setOpen(v => !v)}>
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </>
  );
}

function ParamRow({
  param,
  value,
  file,
  onChange,
  onFileChange,
}: {
  param: ConsoleParameter;
  value: string;
  file?: File | null;
  onChange: (val: string) => void;
  onFileChange?: (file: File | null) => void;
}) {
  return (
    <div className="api-console-param">
      <div className="api-console-param-label">
        <strong>{param.name}</strong>
        <span className="api-console-param-type">{param.type}</span>
        {param.required && <span className="api-console-badge is-auth">required</span>}
      </div>
      <CollapsibleDesc html={param.descriptionHtml} text={param.description} />
      {param.in !== 'body' && param.type !== 'file' && param.example && (
        <div className="api-console-param-example">Example: {param.example}</div>
      )}
      <ParamField
        param={param}
        value={value}
        file={file}
        onChange={onChange}
        onFileChange={onFileChange}
      />
    </div>
  );
}

function MinMaxRow({
  minP,
  maxP,
  values,
  onChange,
}: {
  minP: ConsoleParameter;
  maxP: ConsoleParameter;
  values: Record<string, string>;
  onChange: (name: string, val: string) => void;
}) {
  const desc = minP.descriptionHtml || minP.description || maxP.descriptionHtml || maxP.description;
  return (
    <div className="api-console-param api-console-minmax">
      <div className="api-console-param-label">
        <strong>{minP.name.replace(/^min_?/, '')}</strong>
        <span className="api-console-param-type">range</span>
      </div>
      {desc ? (
        <CollapsibleDesc
          html={minP.descriptionHtml || maxP.descriptionHtml}
          text={minP.description || maxP.description}
        />
      ) : null}
      <div className="api-console-range-inputs">
        <input
          value={values[minP.name] ?? ''}
          onChange={e => onChange(minP.name, e.target.value)}
          placeholder={minP.example ?? 'min'}
        />
        <span className="api-console-range-sep">–</span>
        <input
          value={values[maxP.name] ?? ''}
          onChange={e => onChange(maxP.name, e.target.value)}
          placeholder={maxP.example ?? 'max'}
        />
      </div>
    </div>
  );
}

export default function ApiConsole() {
  const navigate = useNavigate();
  const limitsRef = useRef<HTMLDivElement>(null);
  const responseRef = useRef<HTMLDivElement>(null);
  const { data, loading, error, reload } = useOpenApiSpec();
  const { isAuthenticated } = useAuthStatus();
  const { user, userApiKey: contextUserApiKey, setUserApiKey: contextSetUserApiKey } = userAppContext();
  const { userApiKey } = useUserApiKey({
    user,
    userApiKey: contextUserApiKey,
    setUserApiKey: contextSetUserApiKey,
  });

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [fileValues, setFileValues] = useState<Record<string, File | null>>({});
  const [apiKeyDraft, setApiKeyDraft] = useState('');
  const [authorizedKey, setAuthorizedKey] = useState('');
  const [keyVisible, setKeyVisible] = useState(false);
  const [snippetLang, setSnippetLang] = useState<CodeSnippetLang>('curl');
  const [responseTab, setResponseTab] = useState<'body' | 'headers'>('body');
  const [wrap, setWrap] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending] = useState(false);
  const [showLimits, setShowLimits] = useState(false);
  const [authMessage, setAuthMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [requestContentType, setRequestContentType] = useState('application/json');
  const [responseContentType, setResponseContentType] = useState('*/*');
  const [theme, setTheme] = useState<ConsoleTheme>(() => readStoredTheme());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [response, setResponse] = useState<ApiResponseState>({
    status: 200,
    statusText: 'OK',
    timeMs: 446,
    sizeBytes: 8600,
    headers: { 'content-type': 'application/json' },
    bodyText: JSON.stringify(SAMPLE_RESPONSE, null, 2),
    bodyJson: SAMPLE_RESPONSE,
    isSample: true,
  });

  const isAuthorized = Boolean(authorizedKey.trim());

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  useEffect(() => {
    if (userApiKey) {
      setApiKeyDraft(userApiKey);
      setAuthorizedKey(userApiKey);
    }
  }, [userApiKey]);

  useEffect(() => {
    if (!showLimits) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!limitsRef.current?.contains(event.target as Node)) {
        setShowLimits(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowLimits(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showLimits]);

  useEffect(() => {
    if (!data?.operations.length) return;
    if (!selectedId || !data.operations.some(op => op.id === selectedId)) {
      const preferred = data.operations.find(op => op.path.includes('/gda/summary')) ?? data.operations[0];
      setSelectedId(preferred.id);
    }
  }, [data, selectedId]);

  const selected = useMemo(
    () => data?.operations.find(op => op.id === selectedId) ?? null,
    [data, selectedId],
  );

  const paramGroupsList = useMemo(() => (selected ? groupParameters(selected.parameters) : []), [selected]);

  const activeFilters = useMemo(
    () => (selected ? countActiveFilters(selected.parameters, paramValues) : 0),
    [selected, paramValues],
  );

  useEffect(() => {
    if (!data || !selectedId) return;
    const operation = data.operations.find(op => op.id === selectedId);
    if (!operation) return;
    setParamValues(initialParamValues(operation));
    setFileValues({});
    setRequestContentType(operation.consumes[0] ?? 'application/json');
    setResponseContentType(operation.produces[0] ?? '*/*');
    // Reset collapsed groups: non-defaultOpen groups start collapsed
    const groups = groupParameters(operation.parameters);
    setCollapsedGroups(new Set(groups.filter(g => !g.defaultOpen).map(g => g.key)));
    setResponse(prev =>
      prev.isSample
        ? prev
        : {
            status: null,
            statusText: '',
            timeMs: null,
            sizeBytes: null,
            headers: {},
            bodyText: '',
            bodyJson: null,
            isSample: false,
          },
    );
  }, [data, selectedId]);

  const filteredGroups = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.groups
      .map(group => ({
        ...group,
        operations: group.operations.filter(op => {
          if (!q) return true;
          return (
            op.shortPath.toLowerCase().includes(q) ||
            op.path.toLowerCase().includes(q) ||
            op.summary.toLowerCase().includes(q) ||
            op.method.includes(q) ||
            group.label.toLowerCase().includes(q)
          );
        }),
      }))
      .filter(group => group.operations.length > 0);
  }, [data, search]);

  const requestUrl = useMemo(() => {
    if (!data || !selected) return '';
    return buildRequestUrl(data.baseUrl, selected, paramValues);
  }, [data, selected, paramValues]);

  const bodyParamName = selected?.parameters.find(p => p.in === 'body')?.name;
  const bodyValue = bodyParamName ? paramValues[bodyParamName] || '{}' : undefined;
  const multipart = selected ? isMultipartOperation(selected) : false;
  const effectiveApiKey = authorizedKey || apiKeyDraft;

  const snippets = useMemo(
    () =>
      buildCodeSnippets({
        method: selected?.method ?? 'get',
        url: requestUrl,
        apiKey: effectiveApiKey,
        accept: responseContentType,
        contentType: requestContentType,
        body: multipart ? undefined : bodyValue,
        multipart:
          multipart && selected
            ? selected.parameters
                .filter(p => p.in === 'formData')
                .map(p => ({
                  name: p.name,
                  value:
                    p.type === 'file'
                      ? fileValues[p.name]?.name
                        ? `@${fileValues[p.name]!.name}`
                        : '@your-file.txt'
                      : paramValues[p.name] || undefined,
                  isFile: p.type === 'file',
                }))
            : undefined,
      }),
    [
      selected,
      requestUrl,
      effectiveApiKey,
      bodyValue,
      responseContentType,
      requestContentType,
      multipart,
      paramValues,
      fileValues,
    ],
  );

  const copyText = useCallback(async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      /* ignore */
    }
  }, []);

  const authorize = useCallback(() => {
    const key = apiKeyDraft.trim();
    if (!key) {
      setAuthorizedKey('');
      setAuthMessage('Paste your API key first.');
      return;
    }
    setAuthorizedKey(key);
    setAuthMessage('Authorized. You can Try it out on protected endpoints.');
  }, [apiKeyDraft]);

  const fillExamples = useCallback((operation: ConsoleOperation) => {
    setParamValues(initialParamValues(operation));
    setFileValues({});
  }, []);

  const resetParams = useCallback(() => {
    if (!selected) return;
    const empty: Record<string, string> = {};
    for (const p of selected.parameters) {
      empty[p.name] = '';
    }
    setParamValues(empty);
    setFileValues({});
  }, [selected]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const setParamValue = useCallback((name: string, val: string) => {
    setParamValues(prev => ({ ...prev, [name]: val }));
  }, []);

  const setFileValue = useCallback((name: string, file: File | null) => {
    setFileValues(prev => ({ ...prev, [name]: file }));
    setParamValues(prev => ({ ...prev, [name]: file?.name ?? '' }));
  }, []);

  const sendRequest = useCallback(async () => {
    if (!selected || !requestUrl) return;
    if (selected.requiresAuth && !authorizedKey.trim()) {
      setAuthMessage('Authorize with your API key before calling this endpoint.');
      return;
    }

    setSending(true);
    const started = performance.now();
    try {
      const headers: Record<string, string> = {
        accept: responseContentType,
      };
      if (authorizedKey.trim()) {
        headers.Authorization = authorizedKey.trim();
      }
      const init: RequestInit = { method: selected.method.toUpperCase(), headers };
      const useMultipart =
        isMultipartOperation(selected) && ['post', 'put', 'patch'].includes(selected.method);

      if (useMultipart) {
        // Browser sets multipart boundary; do not set Content-Type manually.
        init.body = buildMultipartFormData(selected, paramValues, fileValues);
      } else if (bodyValue && ['post', 'put', 'patch'].includes(selected.method)) {
        headers['Content-Type'] = requestContentType;
        init.body = bodyValue;
      }

      const res = await fetch(requestUrl, init);
      const text = await res.text();
      const elapsed = Math.round(performance.now() - started);
      let json: unknown = null;
      const wantsJson =
        responseContentType.includes('json') ||
        responseContentType === '*/*' ||
        (res.headers.get('content-type') ?? '').includes('json');
      if (wantsJson) {
        try {
          json = text ? JSON.parse(text) : null;
        } catch {
          json = null;
        }
      }

      const responseHeaders: Record<string, string> = {};
      res.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      setResponse({
        status: res.status,
        statusText: res.statusText || (res.ok ? 'OK' : 'Error'),
        timeMs: elapsed,
        sizeBytes: new Blob([text]).size,
        headers: responseHeaders,
        bodyText: json ? JSON.stringify(json, null, 2) : text,
        bodyJson: json,
        isSample: false,
      });
      setResponseTab('body');
      requestAnimationFrame(() => {
        responseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    } catch (err) {
      const elapsed = Math.round(performance.now() - started);
      setResponse({
        status: null,
        statusText: 'Network error',
        timeMs: elapsed,
        sizeBytes: null,
        headers: {},
        bodyText: err instanceof Error ? err.message : 'Request failed',
        bodyJson: null,
        isSample: false,
        error: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setSending(false);
    }
  }, [
    selected,
    requestUrl,
    authorizedKey,
    bodyValue,
    requestContentType,
    responseContentType,
    paramValues,
    fileValues,
  ]);

  const responseLines = response.bodyText ? response.bodyText.split('\n').length : 0;

  const canTry = Boolean(selected) && (!selected?.requiresAuth || isAuthorized) && !sending;

  if (loading) {
    return (
      <div className={`api-console api-console--${theme}`}>
        <div className="api-console-loading">Loading API documentation…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`api-console api-console--${theme}`}>
        <div className="api-console-error">
          <p>{error ?? 'Unable to load API documentation.'}</p>
          <button type="button" onClick={reload}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  function renderParamGroup(params: ConsoleParameter[]) {
    const rendered: React.ReactNode[] = [];
    const consumed = new Set<string>();

    for (let i = 0; i < params.length; i++) {
      const p = params[i];
      if (consumed.has(p.name)) continue;

      const next = params[i + 1];
      if (next && !consumed.has(next.name) && isMinMaxPair(p.name, next.name)) {
        consumed.add(p.name);
        consumed.add(next.name);
        rendered.push(
          <MinMaxRow
            key={`${p.name}-${next.name}`}
            minP={p}
            maxP={next}
            values={paramValues}
            onChange={setParamValue}
          />,
        );
        continue;
      }

      consumed.add(p.name);
      rendered.push(
        <ParamRow
          key={`${p.in}-${p.name}`}
          param={p}
          value={paramValues[p.name] ?? ''}
          file={fileValues[p.name]}
          onChange={val => setParamValue(p.name, val)}
          onFileChange={file => setFileValue(p.name, file)}
        />,
      );
    }
    return rendered;
  }

  return (
    <div className={`api-console api-console--${theme}`}>
      <header className="api-console-header">
        <div className="api-console-brand">
          <button type="button" onClick={() => navigate('/Tools?section=console')} aria-label="Back to Tools">
            <ArrowLeftIcon style={{ width: 14, height: 14 }} />
            Back
          </button>
          <span>DISGENET API Console</span>
        </div>

        <div className="api-console-header-actions">
          <div className="api-console-key-row">
            <div className="api-console-key">
              <input
                type={keyVisible ? 'text' : 'password'}
                value={apiKeyDraft}
                onChange={e => {
                  setApiKeyDraft(e.target.value);
                  if (authorizedKey && e.target.value.trim() !== authorizedKey) {
                    setAuthorizedKey('');
                    setAuthMessage(null);
                  }
                }}
                placeholder={isAuthenticated ? 'Paste your API key' : 'Sign in or paste your API key'}
                aria-label="API key"
              />
              <button
                type="button"
                onClick={() => setKeyVisible(v => !v)}
                aria-label={keyVisible ? 'Hide API key' : 'Show API key'}
              >
                {keyVisible ? (
                  <EyeSlashIcon style={{ width: 16, height: 16 }} />
                ) : (
                  <EyeIcon style={{ width: 16, height: 16 }} />
                )}
              </button>
              <button
                type="button"
                onClick={() => copyText('key', apiKeyDraft)}
                disabled={!apiKeyDraft}
                aria-label="Copy API key"
              >
                <ClipboardDocumentIcon style={{ width: 16, height: 16 }} />
              </button>
            </div>

            <button
              type="button"
              className={`api-console-authorize-btn${isAuthorized ? ' is-on' : ''}`}
              onClick={authorize}
            >
              <LockClosedIcon style={{ width: 14, height: 14 }} />
              {isAuthorized ? 'Authorized' : 'Authorize'}
            </button>

            <Link to="/Tools?section=documentation" className="api-console-limits-btn">
              <DocumentTextIcon style={{ width: 14, height: 14 }} />
              Documentation
            </Link>

            <div className="api-console-limits-wrap" ref={limitsRef}>
              <button
                type="button"
                className="api-console-limits-btn"
                aria-expanded={showLimits}
                aria-haspopup="dialog"
                onClick={() => setShowLimits(v => !v)}
              >
                <ChartBarIcon style={{ width: 14, height: 14 }} />
                Usage limits
              </button>
              {showLimits && (
                <div className="api-console-limits-popover" role="dialog" aria-label="Usage limits">
                  <div className="api-console-limits-popover-header">
                    <strong>Usage limits</strong>
                    <button type="button" onClick={() => setShowLimits(false)} aria-label="Close">
                      ×
                    </button>
                  </div>
                  <QueriesLimitTable compact />
                </div>
              )}
            </div>

            <button
              type="button"
              className="api-console-theme-btn"
              onClick={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? (
                <SunIcon style={{ width: 16, height: 16 }} />
              ) : (
                <MoonIcon style={{ width: 16, height: 16 }} />
              )}
            </button>

            {!isAuthenticated && (
              <Link to="/login" className="api-console-limits-btn">
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      {authMessage && <div className="api-console-auth-banner">{authMessage}</div>}

      <div className="api-console-body">
        <aside className="api-console-sidebar">
          <div className="api-console-search">
            <MagnifyingGlassIcon />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search endpoints"
              aria-label="Search endpoints"
            />
          </div>
          <nav className="api-console-nav" aria-label="API endpoints">
            {filteredGroups.map(group => (
              <div key={group.name} className="api-console-tag">
                <div className="api-console-tag-label">{group.label}</div>
                {group.operations.map(op => (
                  <button
                    key={op.id}
                    type="button"
                    className={`api-console-endpoint${selectedId === op.id ? ' is-active' : ''}`}
                    onClick={() => setSelectedId(op.id)}
                  >
                    <MethodPill method={op.method} />
                    <span className="api-console-endpoint-path">{op.shortPath}</span>
                  </button>
                ))}
              </div>
            ))}
            {filteredGroups.length === 0 && (
              <div className="api-console-empty">No endpoints match your search.</div>
            )}
          </nav>
        </aside>

        <section className="api-console-main">
          {selected ? (
            <>
              {/* Sticky endpoint bar */}
              <div className="api-console-sticky-bar">
                <MethodPill method={selected.method} />
                <code className="api-console-sticky-path">{selected.path}</code>
                {selected.requiresAuth && !isAuthorized && (
                  <span className="api-console-badge is-auth" style={{ fontSize: 10 }}>
                    Auth
                  </span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                  {activeFilters > 0 && (
                    <span className="api-console-filter-count mt-2">
                      <FunnelIcon style={{ width: 12, height: 12 }} />
                      {activeFilters}
                    </span>
                  )}
                  <button
                    type="button"
                    className="api-console-send api-console-send--compact"
                    onClick={sendRequest}
                    disabled={!canTry}
                  >
                    <PlayIcon style={{ width: 14, height: 14 }} />
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>

              <div className="api-console-endpoint-header">
                <h1 className="api-console-title">{selected.summary}</h1>
                <EndpointDescription html={selected.descriptionHtml} text={selected.descriptionText} />
              </div>

              <div className="api-console-workspace">
                {/* Left column: parameters */}
                <div className="api-console-params-col">
                  <div className="api-console-card">
                    <div className="api-console-card-title">
                      Parameters
                      <span>
                        {activeFilters > 0 && (
                          <span className="api-console-filter-count">
                            <FunnelIcon style={{ width: 11, height: 11 }} />
                            {activeFilters} active
                          </span>
                        )}
                        <button
                          type="button"
                          className="api-console-fill-examples"
                          onClick={() => fillExamples(selected)}
                        >
                          Load examples
                        </button>
                        {activeFilters > 0 && (
                          <button type="button" className="api-console-fill-examples" onClick={resetParams}>
                            <ArrowPathIcon style={{ width: 11, height: 11 }} />
                            Reset
                          </button>
                        )}
                        <button
                          type="button"
                          className="api-console-fill-examples"
                          onClick={() => {
                            const allKeys = paramGroupsList.map(g => g.key);
                            const allCollapsed = allKeys.every(k => collapsedGroups.has(k));
                            setCollapsedGroups(allCollapsed ? new Set() : new Set(allKeys));
                          }}
                        >
                          {paramGroupsList.every(g => collapsedGroups.has(g.key))
                            ? 'Expand all'
                            : 'Collapse all'}
                        </button>
                      </span>
                    </div>

                    {paramGroupsList.map(group => {
                      const isCollapsed = collapsedGroups.has(group.key);
                      const groupActive = countActiveFilters(group.params, paramValues);
                      return (
                        <div key={group.key} className="api-console-param-group">
                          <button
                            type="button"
                            className={`api-console-group-toggle${isCollapsed ? '' : ' is-open'}`}
                            onClick={() => toggleGroup(group.key)}
                            aria-expanded={!isCollapsed}
                          >
                            <ChevronDownIcon className="api-console-group-chevron" />
                            <span>{group.label}</span>
                            <span className="api-console-group-meta">
                              {group.params.length}
                              {groupActive > 0 && (
                                <span className="api-console-filter-count is-small">{groupActive}</span>
                              )}
                            </span>
                          </button>
                          {!isCollapsed && (
                            <div className="api-console-group-body">{renderParamGroup(group.params)}</div>
                          )}
                        </div>
                      );
                    })}

                    <div className="api-console-content-types">
                      {selected.consumes.length > 0 && selected.parameters.some(p => p.in === 'body') && (
                        <label className="api-console-content-type">
                          <span>Parameter content type</span>
                          <select
                            value={requestContentType}
                            onChange={e => setRequestContentType(e.target.value)}
                          >
                            {selected.consumes.map(type => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      {selected.produces.length > 0 && (
                        <label className="api-console-content-type">
                          <span>Response content type</span>
                          <select
                            value={responseContentType}
                            onChange={e => setResponseContentType(e.target.value)}
                          >
                            {selected.produces.map(type => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </div>

                    <button
                      type="button"
                      className="api-console-send"
                      onClick={sendRequest}
                      disabled={!canTry}
                      title={
                        selected.requiresAuth && !isAuthorized
                          ? 'Authorize with your API key first'
                          : undefined
                      }
                    >
                      <PlayIcon style={{ width: 14, height: 14 }} />
                      {sending ? 'Sending…' : 'Try it out'}
                    </button>
                    {selected.requiresAuth && !isAuthorized && (
                      <p className="api-console-send-hint">
                        Click <strong>Authorize</strong> in the header with your API key before trying this
                        endpoint.
                      </p>
                    )}
                  </div>
                </div>

                {/* Right column: Request + Response (sticky) */}
                <div className="api-console-right-col">
                  <div className="api-console-card">
                    <div className="api-console-card-title">Request</div>
                    <div className="api-console-url-box">
                      <code>{requestUrl}</code>
                      <button
                        type="button"
                        className="api-console-copy"
                        style={{ position: 'static' }}
                        onClick={() => copyText('url', requestUrl)}
                      >
                        {copied === 'url' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="api-console-tabs" role="tablist" aria-label="Code snippets">
                      {CODE_SNIPPET_LANGS.map(lang => (
                        <button
                          key={lang}
                          type="button"
                          role="tab"
                          aria-selected={snippetLang === lang}
                          className={snippetLang === lang ? 'is-active' : undefined}
                          onClick={() => setSnippetLang(lang)}
                        >
                          {CODE_SNIPPET_LABELS[lang]}
                        </button>
                      ))}
                    </div>
                    <div className="api-console-code">
                      <button
                        type="button"
                        className="api-console-copy"
                        onClick={() => copyText('snippet', snippets[snippetLang])}
                      >
                        {copied === 'snippet' ? 'Copied' : 'Copy'}
                      </button>
                      <pre>{snippets[snippetLang]}</pre>
                    </div>
                  </div>

                  <div className={`api-console-response${expanded ? ' is-expanded' : ''}`} ref={responseRef}>
                    <div className="api-console-response-bar">
                      <span
                        className={
                          response.status != null && response.status < 400
                            ? 'api-console-status-ok'
                            : 'api-console-status-err'
                        }
                      >
                        {response.status != null
                          ? `${response.status} ${response.statusText}`
                          : response.statusText || '—'}
                      </span>
                      <span>Time {response.timeMs != null ? `${response.timeMs} ms` : '—'}</span>
                      <span>Size {formatBytes(response.sizeBytes)}</span>
                      <span>Lines {responseLines || '—'}</span>
                      <div className="api-console-response-tools">
                        <button
                          type="button"
                          className={responseTab === 'body' ? 'is-active' : undefined}
                          onClick={() => setResponseTab('body')}
                        >
                          Body
                        </button>
                        <button
                          type="button"
                          className={responseTab === 'headers' ? 'is-active' : undefined}
                          onClick={() => setResponseTab('headers')}
                        >
                          Headers
                        </button>
                        <button
                          type="button"
                          className={wrap ? 'is-active' : undefined}
                          onClick={() => setWrap(v => !v)}
                        >
                          Wrap
                        </button>
                        <button type="button" onClick={() => setExpanded(v => !v)}>
                          {expanded ? 'Collapse' : 'Expand'}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            copyText(
                              'response',
                              responseTab === 'body'
                                ? response.bodyText
                                : JSON.stringify(response.headers, null, 2),
                            )
                          }
                        >
                          {copied === 'response' ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    <div
                      className={`api-console-response-body${wrap ? ' wrap' : ''}${expanded ? ' is-expanded' : ''}`}
                    >
                      <pre>
                        {responseTab === 'body'
                          ? response.bodyText || 'No response yet.'
                          : JSON.stringify(response.headers, null, 2)}
                      </pre>
                    </div>
                    {response.isSample && (
                      <div className="api-console-response-note">
                        Sample payload from the DISGENET schema — authorize and Try it out to fetch live data.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="api-console-empty">Select an endpoint to get started.</div>
          )}
        </section>
      </div>
    </div>
  );
}
